// src/services/centralContext.ts
//
// Central Context Service for Frontend — Consumer-Side Helpers
//
// ============================================================================
// ARCHITECTURE: Central is the SINGLE SOURCE OF TRUTH
// ============================================================================
//
// Pure authorization logic (roles, scopes, permissions, entitlements,
// capabilities) is provided by @levelup/central-context.
// This file re-exports those functions and adds CRM-specific wrappers
// (APP_ID binding), React hooks, and credit operations.
//
// CRITICAL: CreditOperationBlockedError (503) must BLOCK operations — NO fallback.
// ============================================================================

import { CENTRAL_URL, APP_ID } from '../config/centralAuth';
import type {
  SwitchContextWorkspace,
  SuiteApp,
  SwitchContextUser,
  SwitchContextMeta,
  CapabilitiesMap,
  WorkspacePermissions,
} from '../store/slices/bootContextSlice';

// ============================================================
// @levelup/central-context — shared pure functions
// ============================================================

import {
  // Entitlements
  hasAppAccess,
  assertEntitledOrBlock as _assertEntitledOrBlock,

  // Capabilities (shared takes appId param — CRM wraps with APP_ID)
  hasCapability as _hasCapability,
  requireCapability as _requireCapability,

  // Permissions (identical signatures — re-export directly)
  hasWorkspacePermission,
  requireWorkspacePermission,

  // Roles (identical signatures — re-export directly)
  ROLE_HIERARCHY,
  hasMinimumRole,
  hasRole,
  requireRole,
  requireMinimumRole,

  // Scopes (identical signatures — re-export directly)
  hasScope,
  hasScopes,
  requireScopes,

  // Errors (re-export shared errors)
  CentralUnavailableError,
  UnauthorizedError,
  NotEntitledError,
  InsufficientRoleError,
  MissingScopeError,
} from '@levelup/central-context';

import type {
  CentralRole,
  WorkspacePermissionKey,
} from '@levelup/central-context';

// ============================================================
// Re-exports — maintain backward compatibility for CRM consumers
// ============================================================

// Re-export shared pure functions
export {
  hasAppAccess,
  hasWorkspacePermission,
  requireWorkspacePermission,
  ROLE_HIERARCHY,
  hasMinimumRole,
  hasRole,
  requireRole,
  requireMinimumRole,
  hasScope,
  hasScopes,
  requireScopes,
};

// Re-export shared types
export type { CentralRole, WorkspacePermissionKey };

// Re-export shared errors
export {
  CentralUnavailableError,
  UnauthorizedError,
  NotEntitledError,
  InsufficientRoleError,
  MissingScopeError,
};

// Re-export CRM store types for convenience
export type {
  SwitchContextWorkspace,
  SuiteApp,
  SwitchContextUser,
  SwitchContextMeta,
  CapabilitiesMap,
  WorkspacePermissions,
};

// ============================================================
// CRM-specific Types
// ============================================================

export interface CentralContext {
  currentWorkspace: SwitchContextWorkspace;
  otherWorkspaces: SwitchContextWorkspace[];
  apps: SuiteApp[];
  user: SwitchContextUser;
  meta?: SwitchContextMeta | null;
}

// ============================================================
// CRM-specific Error Classes (not in shared package)
// ============================================================

/**
 * CRM-scoped CapabilityDeniedError — uses APP_ID automatically.
 * The shared package version takes an explicit appId parameter.
 */
export class CapabilityDeniedError extends Error {
  public readonly domain: string;
  public readonly action: string;

  constructor(domain: string, action: string) {
    super(`Capability denied: ${APP_ID}:${domain}:${action}`);
    this.name = 'CapabilityDeniedError';
    this.domain = domain;
    this.action = action;
  }
}

/**
 * Credit operation failed — Central returned 503 SERVICE_DEGRADED.
 * CRITICAL: This error MUST block the operation. NO fallback allowed.
 */
export class CreditOperationBlockedError extends Error {
  public readonly statusCode = 503;
  public readonly errorCode = 'SERVICE_DEGRADED';

  constructor(message: string) {
    super(message);
    this.name = 'CreditOperationBlockedError';
  }
}

export class InsufficientCreditsError extends Error {
  public readonly required: number;
  public readonly available: number;

  constructor(required: number, available: number) {
    super(`Insufficient credits. Required: ${required}, available: ${available}`);
    this.name = 'InsufficientCreditsError';
    this.required = required;
    this.available = available;
  }
}

// ============================================================
// CRM-specific Wrappers (bind APP_ID to shared functions)
// ============================================================

/**
 * Check if the user has the current app (APP_ID) entitlement.
 * This is the primary entitlement check for LevelUp CRM.
 */
export function hasCurrentAppAccess(context: CentralContext): boolean {
  return hasAppAccess(context, APP_ID);
}

/**
 * Assert that the user has access to the current app.
 * @throws {NotEntitledError} if not entitled
 */
export function assertEntitledOrBlock(context: CentralContext): void {
  _assertEntitledOrBlock(context, APP_ID);
}

/**
 * Check if the user has a specific capability for this CRM app.
 * Delegates to shared hasCapability with APP_ID.
 */
export function hasCapability(
  context: CentralContext,
  domain: string,
  action: string
): boolean {
  return _hasCapability(context, APP_ID, domain, action);
}

/**
 * Assert that the user has a specific capability for this CRM app.
 * @throws {CapabilityDeniedError} if capability is not granted
 */
export function requireCapability(
  context: CentralContext,
  domain: string,
  action: string
): void {
  if (!hasCapability(context, domain, action)) {
    throw new CapabilityDeniedError(domain, action);
  }
}

// ============================================================
// Credit Operations Types
// ============================================================

export interface CreditReserveRequest {
  workspaceId: string;
  amount: number;
  idempotencyKey: string;
  reason?: string;
}

export interface CreditReserveResponse {
  reservationId: string;
  amount: number;
  expiresAt: string;
  /**
   * True when Central answered with an EXISTING reservation for this
   * idempotency key (HTTP 200 replay) rather than holding credits anew
   * (201). The operation was already reserved — and possibly already
   * committed — by an earlier attempt; committing again is idempotent and
   * does not charge twice.
   */
  replayed: boolean;
}

export interface CreditCommitRequest {
  reservationId: string;
  actualAmount?: number;
}

export interface CreditCommitResponse {
  consumed: number;
  remainingBalance: number;
}

export interface CreditReleaseRequest {
  reservationId: string;
}

export interface CreditReleaseResponse {
  released: number;
  remainingBalance: number;
}

export interface CreditBalanceResponse {
  balance: number;
  reserved: number;
  available: number;
}

// ============================================================
// Configuration
// ============================================================

const CREDIT_API_TIMEOUT_MS = 5_000;

// ============================================================
// Credit Operations — Reserve/Commit/Release Pattern
// ============================================================

/**
 * Reserve credits for an operation.
 * The idempotencyKey ensures the same operation isn't charged twice.
 *
 * CRITICAL: If this returns a CreditOperationBlockedError (503),
 * the caller MUST NOT proceed with the operation. NO fallback allowed.
 *
 * @throws {CreditOperationBlockedError} on 503 — MUST block operation
 * @throws {InsufficientCreditsError} on insufficient balance
 * @throws {CentralUnavailableError} on other errors
 */
export async function reserveCredits(
  accessToken: string,
  request: CreditReserveRequest
): Promise<CreditReserveResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CREDIT_API_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/credits/reserve`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-App-Id': APP_ID,
        'X-Idempotency-Key': request.idempotencyKey,
      },
      // Central de-dups reserves on the body's `referenceId` (the header is
      // informational). Without it Central refuses the reserve outright, and
      // the deterministic key never reaches the place that de-dups on it.
      body: JSON.stringify({ ...request, referenceId: request.idempotencyKey }),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      return {
        reservationId: data.reservationId,
        amount: data.amount,
        expiresAt: data.expiresAt,
        replayed: response.status === 200,
      };
    }

    // 503 SERVICE_DEGRADED — MUST block, NO fallback
    if (response.status === 503) {
      throw new CreditOperationBlockedError(
        'Credit service unavailable. Operation blocked per Central contract.'
      );
    }

    // 402 — Insufficient credits
    if (response.status === 402) {
      const data = await response.json().catch(() => ({}));
      throw new InsufficientCreditsError(request.amount, data.available ?? 0);
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    const errorText = await response.text().catch(() => '');
    throw new CentralUnavailableError(
      `Credit reserve failed with status ${response.status}: ${errorText}`
    );
  } catch (err) {
    if (
      err instanceof CreditOperationBlockedError ||
      err instanceof InsufficientCreditsError ||
      err instanceof UnauthorizedError ||
      err instanceof CentralUnavailableError
    ) {
      throw err;
    }

    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new CreditOperationBlockedError(
        'Credit reserve request timed out. Operation blocked per Central contract.'
      );
    }

    throw new CentralUnavailableError(
      `Credit reserve failed: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Commit a credit reservation after successful operation.
 */
export async function commitCredits(
  accessToken: string,
  request: CreditCommitRequest
): Promise<CreditCommitResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CREDIT_API_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/credits/commit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-App-Id': APP_ID,
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      return {
        consumed: data.consumed,
        remainingBalance: data.remainingBalance,
      };
    }

    const errorText = await response.text().catch(() => '');
    console.error('[CentralContext] Credit commit failed:', response.status, errorText);

    return {
      consumed: request.actualAmount ?? 0,
      remainingBalance: -1,
    };
  } catch (err) {
    console.error('[CentralContext] Credit commit error:', err);
    return {
      consumed: request.actualAmount ?? 0,
      remainingBalance: -1,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Release a credit reservation (operation was cancelled/failed).
 */
export async function releaseCredits(
  accessToken: string,
  request: CreditReleaseRequest
): Promise<CreditReleaseResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CREDIT_API_TIMEOUT_MS);

  try {
    const response = await fetch(`${CENTRAL_URL}/api/credits/release`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-App-Id': APP_ID,
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    if (response.ok) {
      const data = await response.json();
      return {
        released: data.released,
        remainingBalance: data.remainingBalance,
      };
    }

    const errorText = await response.text().catch(() => '');
    console.error('[CentralContext] Credit release failed:', response.status, errorText);

    return {
      released: 0,
      remainingBalance: -1,
    };
  } catch (err) {
    console.error('[CentralContext] Credit release error:', err);
    return {
      released: 0,
      remainingBalance: -1,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get the current credit balance for a workspace.
 */
export async function getCreditBalance(
  accessToken: string,
  workspaceId: string
): Promise<CreditBalanceResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CREDIT_API_TIMEOUT_MS);

  try {
    const response = await fetch(
      `${CENTRAL_URL}/api/credits/balance?workspaceId=${encodeURIComponent(workspaceId)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-App-Id': APP_ID,
        },
        signal: controller.signal,
      }
    );

    if (response.ok) {
      const data = await response.json();
      return {
        balance: data.balance ?? 0,
        reserved: data.reserved ?? 0,
        available: data.available ?? data.balance ?? 0,
      };
    }

    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    throw new CentralUnavailableError(`Failed to get credit balance: status ${response.status}`);
  } catch (err) {
    if (err instanceof UnauthorizedError || err instanceof CentralUnavailableError) {
      throw err;
    }

    throw new CentralUnavailableError(
      `Failed to get credit balance: ${err instanceof Error ? err.message : 'Unknown error'}`
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================
// React Hooks (CRM-specific — use useAppSelector)
// ============================================================

import { useAppSelector } from '../store/store';
import {
  selectBootCurrentWorkspace,
  selectBootOtherWorkspaces,
  selectBootApps,
  selectBootUser,
  selectBootMeta,
  selectBootStatus,
} from '../store/slices/bootContextSlice';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';

/**
 * Hook to get the current Central context from Redux boot state.
 * Returns null if not ready.
 */
export function useCentralContext(): CentralContext | null {
  const bootStatus = useAppSelector(selectBootStatus);
  const currentWorkspace = useAppSelector(selectBootCurrentWorkspace);
  const otherWorkspaces = useAppSelector(selectBootOtherWorkspaces);
  const apps = useAppSelector(selectBootApps);
  const user = useAppSelector(selectBootUser);
  const meta = useAppSelector(selectBootMeta);

  if (bootStatus !== 'ready' || !currentWorkspace || !user) {
    return null;
  }

  return {
    currentWorkspace,
    otherWorkspaces,
    apps,
    user,
    meta,
  };
}

/**
 * Hook to check if user has app entitlement.
 */
export function useHasAppAccess(appId: string = APP_ID): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasAppAccess(context, appId);
}

/**
 * Hook to check if user has a specific capability.
 */
export function useHasCapability(domain: string, action: string): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasCapability(context, domain, action);
}

/**
 * Hook to check if user has a specific workspace permission.
 */
export function useHasWorkspacePermission(permission: WorkspacePermissionKey): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasWorkspacePermission(context, permission);
}

/**
 * Hook to check if user has required role.
 */
export function useHasRole(roles: CentralRole[]): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasRole(context, roles);
}

/**
 * Hook to check if user has minimum role.
 */
export function useHasMinimumRole(minimumRole: CentralRole): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasMinimumRole(context, minimumRole);
}

/**
 * Hook to check if user has required scopes.
 * Note: Use useHasCapability() instead when possible.
 */
export function useHasScopes(requiredScopes: string[]): boolean {
  const context = useCentralContext();
  if (!context) return false;
  return hasScopes(context, requiredScopes);
}

/**
 * Hook to get access token for credit operations.
 */
export function useCentralAccessToken(): string | null {
  return useAppSelector(selectCentralAccessToken);
}

// ============================================================
// Exports
// ============================================================

export const CentralContextService = {
  // Entitlements
  hasAppAccess,
  hasCurrentAppAccess,
  assertEntitledOrBlock,

  // Capabilities (Central-derived)
  hasCapability,
  requireCapability,

  // Workspace Permissions (Central-derived)
  hasWorkspacePermission,
  requireWorkspacePermission,

  // Authorization - Roles
  hasRole,
  hasMinimumRole,
  requireRole,
  requireMinimumRole,

  // Authorization - Scopes (exact match only)
  hasScope,
  hasScopes,
  requireScopes,

  // Credits
  reserveCredits,
  commitCredits,
  releaseCredits,
  getCreditBalance,
};
